package com.milta.core.ui.component

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Check
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.Schedule
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.milta.core.localization.CommonStrings
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.ProvideAppStrings
import com.milta.core.ui.theme.InProgressBg
import com.milta.core.ui.theme.InProgressInk
import com.milta.core.ui.theme.MiltaTheme
import com.milta.core.ui.theme.SuccessInk
import kotlinx.coroutines.delay
import kotlin.time.Clock

/**
 * Carte "analyse en cours" partagée par les écrans d'import (photo OCR, PDF, tableur).
 *
 * Remplace le spinner seul, qui donnait l'impression que l'app était figée sur les analyses longues :
 * aperçu de document balayé, 4 étapes cochées une à une, chrono, puis un message rassurant une fois la
 * durée habituelle dépassée.
 *
 * L'avancement des étapes est ESTIMÉ à partir du temps écoulé ([stepEndsSeconds]) : l'appel backend
 * (extraction + validation HERE) est un seul aller-retour sans progression. La dernière étape reste
 * "en cours" jusqu'à ce que l'écran quitte la phase d'analyse — on ne coche jamais tout avant la vraie
 * réponse.
 */
@Composable
fun MiltaProcessingCard(
    fileName: String,
    fileSizeLabel: String,
    modifier: Modifier = Modifier,
    /** Fin estimée (en secondes écoulées) des 3 premières étapes. */
    stepEndsSeconds: List<Int> = DefaultStepEndsSeconds,
    /** Au-delà, le message de durée habituelle est remplacé par [com.milta.core.localization.CommonStrings.processingLongWait]. */
    longWaitAfterSeconds: Int = DefaultLongWaitAfterSeconds,
    /** Remplace [com.milta.core.localization.CommonStrings.processingUsualDuration] (imports asynchrones : l'analyse continue hors de l'écran). */
    usualDurationNote: String? = null,
    /** Début réel de l'analyse (epoch ms) : le chrono part de là au lieu de 0 — un écran rouvert reprend où en est l'analyse. */
    startedAtEpochMillis: Long? = null,
    /** Le document a déjà été lu (relance) : les 3 premières étapes sont cochées, seule la dernière tourne. */
    resumeAtLastStep: Boolean = false,
    /** Envoi en cours (avant la réponse 202) : la 1re étape est active et montre la progression réelle. */
    upload: ProcessingUpload? = null,
    /** Libellé de la 1re étape une fois l'envoi fait (ex. « Photo envoyée ») ; défaut [com.milta.core.localization.CommonStrings.processingStepSent]. */
    sentStepLabel: String? = null,
    /** Remplace la note sous les étapes (bandeau d'envoi). */
    footer: (@Composable () -> Unit)? = null,
    /**
     * Retour 26/10 — progression réelle envoyée par le serveur : les étapes suivent l'étape réelle au
     * lieu du chrono (R1 à R8). Null = serveur pas encore à jour, estimation au chrono inchangée.
     */
    realProgress: ProcessingProgress? = null,
) {
    val strings = LocalStrings.current.common
    fun secondsSinceStart(): Int =
        startedAtEpochMillis?.let { ((Clock.System.now().toEpochMilliseconds() - it) / 1_000).toInt().coerceAtLeast(0) } ?: 0
    var elapsedSeconds by remember(startedAtEpochMillis) { mutableIntStateOf(secondsSinceStart()) }
    LaunchedEffect(startedAtEpochMillis) {
        while (true) {
            delay(1_000)
            elapsedSeconds = if (startedAtEpochMillis != null) secondsSinceStart() else elapsedSeconds + 1
        }
    }

    val labels = listOf(
        upload?.stepLabel ?: sentStepLabel ?: strings.processingStepSent,
        strings.processingStepReading,
        strings.processingStepFinding,
        strings.processingStepChecking,
    )
    // Real progress only once the file is on the server; the upload keeps its own first step.
    val real = realProgress.takeIf { upload == null }
    val activeIndex = when {
        upload != null -> 0
        real != null -> real.activeStepIndex
        resumeAtLastStep -> labels.size - 1
        else -> activeStepIndex(elapsedSeconds, stepEndsSeconds, labels.size)
    }
    // While sending, the long-wait message would be wrong: nothing is analysed yet.
    // R8 — the real step is known: no "long wait" message based on the chrono.
    val isLongWait = upload == null && real == null && elapsedSeconds >= longWaitAfterSeconds

    MiltaCard(modifier = modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            ScanningDocument()

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text(
                    text = "${labels[activeIndex]}…",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.ExtraBold,
                    color = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.weight(1f).semantics { liveRegion = LiveRegionMode.Polite },
                )
                Text(
                    text = formatElapsed(elapsedSeconds),
                    style = MaterialTheme.typography.labelSmall.copy(fontFeatureSettings = "tnum"),
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                labels.forEachIndexed { index, label ->
                    val sentDetail = if (index != 0 || upload != null) null else if (fileSizeLabel.isBlank()) fileName else "$fileName · $fileSizeLabel"
                    ProcessingStep(
                        label = label,
                        detail = if (index == 0 || real == null) sentDetail else real.stepDetail(index, strings),
                        state = when {
                            index < activeIndex -> StepState.DONE
                            index == activeIndex -> StepState.ACTIVE
                            else -> StepState.PENDING
                        },
                        progress = upload?.takeIf { index == 0 } ?: real?.addressProgress(strings)?.takeIf { index == ADDRESS_STEP_INDEX },
                        detailHighlighted = real != null && index == FINDING_STEP_INDEX,
                    )
                }
            }

            if (footer != null) {
                footer()
            } else AnimatedContent(
                targetState = isLongWait,
                transitionSpec = { fadeIn() togetherWith fadeOut() },
                label = "processing-note",
            ) { longWait ->
                if (longWait) {
                    ProcessingNote(
                        text = strings.processingLongWait,
                        icon = Icons.Outlined.Info,
                        background = MaterialTheme.colorScheme.primaryContainer,
                        contentColor = MaterialTheme.colorScheme.onSurface,
                        iconTint = InProgressInk,
                    )
                } else if (usualDurationNote != null) {
                    // Maquette imports asynchrones : note d'information (fond accent, icône info).
                    MiltaInfoNote(text = usualDurationNote)
                } else {
                    ProcessingNote(
                        text = strings.processingUsualDuration,
                        icon = Icons.Outlined.Schedule,
                        background = MaterialTheme.colorScheme.background,
                        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        iconTint = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

internal val DefaultStepEndsSeconds = listOf(2, 8, 22)
internal const val DefaultLongWaitAfterSeconds = 45

/** Étape active d'après le temps écoulé ; plafonnée à la dernière, qui reste active jusqu'à la vraie réponse. */
internal fun activeStepIndex(elapsedSeconds: Int, stepEndsSeconds: List<Int>, stepCount: Int): Int {
    val index = stepEndsSeconds.indexOfFirst { elapsedSeconds < it }
    return if (index == -1) stepCount - 1 else index.coerceAtMost(stepCount - 1)
}

internal fun formatElapsed(seconds: Int): String =
    "${seconds / 60}:${(seconds % 60).toString().padStart(2, '0')}"

/**
 * Envoi du fichier en cours, d'après les octets réellement envoyés (pas d'estimation).
 * [sentLabel]/[totalLabel] : tailles déjà formatées ; [percent] : 0 à 100.
 */
data class ProcessingUpload(
    val stepLabel: String,
    val sentLabel: String,
    val totalLabel: String,
    val percent: Int,
    /** Gabarit « %1 sur %2 » de la ligne de progression. */
    val progressTemplate: String,
)

/** Real step of the server (retour 26/10): null while the job is still waiting to be picked up. */
enum class ProcessingStage { Reading, Geocoding }

/**
 * Retour 26/10 — progress reported by the server for a running import. [stageStartedAtEpochMillis]
 * starts "En cours depuis m:ss"; [readingDurationSeconds] and [deliveryCount] are known once the
 * document was read; [geocodedCount] / [geocodeTotal] count the addresses checked.
 */
data class ProcessingProgress(
    val stage: ProcessingStage?,
    val stageStartedAtEpochMillis: Long? = null,
    val readingDurationSeconds: Int? = null,
    val deliveryCount: Int? = null,
    val geocodedCount: Int? = null,
    val geocodeTotal: Int? = null,
) {
    /** Pending and Reading: "Lecture du document"; Geocoding: "Vérification des adresses". */
    internal val activeStepIndex: Int
        get() = if (stage == ProcessingStage.Geocoding) ADDRESS_STEP_INDEX else READING_STEP_INDEX

    /** R4 — whole percent, rounded down; null without a total to count against. */
    val percent: Int?
        get() {
            val total = geocodeTotal?.takeIf { it > 0 } ?: return null
            return ((geocodedCount ?: 0).coerceIn(0, total) * 100 / total)
        }

    /** R2/R3 — "En cours depuis m:ss", "Terminée en m:ss", "N livraisons trouvées". */
    fun stepDetail(index: Int, strings: CommonStrings, nowEpochMillis: Long = Clock.System.now().toEpochMilliseconds()): String? = when {
        index == READING_STEP_INDEX && stage == ProcessingStage.Reading -> stageStartedAtEpochMillis?.let {
            strings.processingInProgressSince.replace("%1", formatElapsed(((nowEpochMillis - it) / 1_000).toInt().coerceAtLeast(0)))
        }
        index == READING_STEP_INDEX && stage == ProcessingStage.Geocoding ->
            readingDurationSeconds?.let { strings.processingFinishedIn.replace("%1", formatElapsed(it.coerceAtLeast(0))) }
        index == FINDING_STEP_INDEX && stage == ProcessingStage.Geocoding -> deliveryCount?.let { strings.deliveriesFound(it) }
        else -> null
    }

    /** R4 — the address step's bar, same rendering as the upload's; null without a total. */
    fun addressProgress(strings: CommonStrings): ProcessingUpload? {
        if (stage != ProcessingStage.Geocoding) return null
        val percent = percent ?: return null
        return ProcessingUpload(
            stepLabel = strings.processingStepChecking,
            sentLabel = (geocodedCount ?: 0).toString(),
            totalLabel = geocodeTotal.toString(),
            percent = percent,
            progressTemplate = strings.processingAddressesChecked,
        )
    }
}

/** "163 livraisons trouvées" / "1 livraison trouvée". */
fun CommonStrings.deliveriesFound(count: Int): String =
    if (count == 1) processingDeliveriesFoundOne else processingDeliveriesFound.replace("%1", count.toString())

internal const val READING_STEP_INDEX = 1
internal const val FINDING_STEP_INDEX = 2
internal const val ADDRESS_STEP_INDEX = 3

private enum class StepState { DONE, ACTIVE, PENDING }

@Composable
private fun ProcessingStep(
    label: String,
    detail: String?,
    state: StepState,
    progress: ProcessingUpload? = null,
    /** "163 livraisons trouvées" — a result, shown in the success color (maquette M1). */
    detailHighlighted: Boolean = false,
) {
    Row(
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Box(
            modifier = Modifier
                .size(24.dp)
                .background(
                    color = when (state) {
                        StepState.DONE -> MaterialTheme.colorScheme.primary
                        StepState.ACTIVE -> InProgressBg
                        StepState.PENDING -> MaterialTheme.colorScheme.surfaceVariant
                    },
                    shape = CircleShape,
                ),
            contentAlignment = Alignment.Center,
        ) {
            when (state) {
                StepState.DONE -> Icon(
                    imageVector = Icons.Outlined.Check,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onPrimary,
                    modifier = Modifier.size(16.dp),
                )
                StepState.ACTIVE -> CircularProgressIndicator(
                    modifier = Modifier.size(14.dp),
                    strokeWidth = 2.dp,
                    color = InProgressInk,
                    trackColor = Color.Transparent,
                )
                StepState.PENDING -> Unit
            }
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = label,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = if (state == StepState.PENDING) {
                    MaterialTheme.colorScheme.onSurfaceVariant
                } else {
                    MaterialTheme.colorScheme.onSurface
                },
            )
            if (detail != null) {
                Text(
                    text = detail,
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = if (detailHighlighted) FontWeight.SemiBold else null,
                    color = if (detailHighlighted) SuccessInk else MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (progress != null) {
                Spacer(Modifier.height(8.dp))
                LinearProgressIndicator(
                    progress = { progress.percent / 100f },
                    modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(50)),
                    color = MaterialTheme.colorScheme.primary,
                    trackColor = MaterialTheme.colorScheme.surfaceVariant,
                )
                Spacer(Modifier.height(5.dp))
                Row(modifier = Modifier.fillMaxWidth()) {
                    Text(
                        text = progress.progressTemplate.replace("%1", progress.sentLabel).replace("%2", progress.totalLabel),
                        style = MaterialTheme.typography.labelSmall.copy(fontFeatureSettings = "tnum"),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = "${progress.percent} %",
                        style = MaterialTheme.typography.labelSmall.copy(fontFeatureSettings = "tnum"),
                        fontWeight = FontWeight.Bold,
                        color = InProgressInk,
                    )
                }
            }
        }
    }
}

/** Note d'information (fond accent, icône info) — même rendu que le message "attente longue" de la carte. */
@Composable
fun MiltaInfoNote(text: String, modifier: Modifier = Modifier) {
    ProcessingNote(
        text = text,
        icon = Icons.Outlined.Info,
        background = MaterialTheme.colorScheme.primaryContainer,
        contentColor = MaterialTheme.colorScheme.onSurface,
        iconTint = InProgressInk,
        modifier = modifier,
    )
}

@Composable
private fun ProcessingNote(
    text: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    background: Color,
    contentColor: Color,
    iconTint: Color,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(background, RoundedCornerShape(12.dp))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Icon(imageVector = icon, contentDescription = null, tint = iconTint, modifier = Modifier.size(16.dp))
        Text(text = text, style = MaterialTheme.typography.labelSmall, color = contentColor)
    }
}

/** Feuille stylisée balayée par une ligne teal : montre que le document est en train d'être lu. */
@Composable
private fun ScanningDocument() {
    val transition = rememberInfiniteTransition(label = "processing-scan")
    val progress by transition.animateFloat(
        initialValue = 0.08f,
        targetValue = 0.92f,
        animationSpec = infiniteRepeatable(tween(durationMillis = 2_400, easing = LinearEasing), RepeatMode.Reverse),
        label = "processing-scan-line",
    )
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxWidth()
            .height(112.dp)
            .background(MaterialTheme.colorScheme.background, RoundedCornerShape(12.dp)),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .rotate(-3f)
                .shadow(2.dp, RoundedCornerShape(4.dp))
                .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(4.dp))
                .width(92.dp)
                .height(96.dp)
                .padding(horizontal = 8.dp, vertical = 10.dp),
            verticalArrangement = Arrangement.spacedBy(5.dp),
        ) {
            PaperLineWidths.forEachIndexed { index, fraction ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth(fraction)
                        .height(4.dp)
                        .background(
                            if (index % 3 == 2) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant,
                            RoundedCornerShape(2.dp),
                        ),
                )
            }
        }
        Box(
            modifier = Modifier
                .align(Alignment.TopStart)
                .offset(y = maxHeight * progress)
                .fillMaxWidth()
                .height(2.dp)
                .background(MaterialTheme.colorScheme.primary),
        )
    }
}

private val PaperLineWidths = listOf(0.8f, 0.55f, 1f, 0.8f, 0.55f, 1f, 0.8f, 0.55f, 1f)

@Preview
@Composable
private fun MiltaProcessingCardPreview() {
    MiltaTheme {
        ProvideAppStrings {
            MiltaProcessingCard(fileName = "IMG_2041.jpg", fileSizeLabel = "2,4 Mo")
        }
    }
}
